import * as Sentry from "@sentry/nextjs"
import { Alert, Checkbox, Label, Spinner } from "flowbite-react"
import { FieldArray, Form, Formik, FormikHelpers, FormikProps } from "formik"
import React, { useEffect, useMemo, useState } from "react"
import useSWR, { useSWRConfig } from "swr"
import * as Yup from "yup"

import { CautionIcon } from "@assets/icons"
import useAuth from "@hooks/useAuth"
import useToast from "@hooks/useToast"
import ParticipantsFields from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipantsFields"
import { checkDisplayRules } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipationDetailsForm"
import { PerOrderFieldsFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PerOrderFIeldsModal"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import { ILoggedInCustomerDetails } from "@mono/essentials/types/CustomerTypes"
import { IDomain } from "@mono/essentials/types/UserTypes"

import VerifyMembershipForm from "../VerifyMembershipForm"

export type CustomerDetailsFormAttributes = {
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_code: string
}
interface CustomerDetailsFormProps {
  onSuccess?: (
    customerId: string,
    perOrderParticipantDetails: PerOrderFieldsFormAttributes,
    customerMembershipId?: string
  ) => void
  selectedCustomerId?: string
  isValid?: boolean
  isIntegratedPage?: boolean
  selectedProductId?: string
  productFieldsData?: any
  customerDetailsRef?: React.RefObject<FormikProps<PerOrderFieldsFormAttributes>>
  hideVerificationSection?: boolean
  customerMembershipOptions?: any
  membershipId?: string
  hasMembershipProduct?: boolean
  domainDetails: IDomain
}

const CustomerDetailsForm = ({
  onSuccess,
  isIntegratedPage = false,
  selectedProductId,
  customerDetailsRef,
  hideVerificationSection = false,
  hasMembershipProduct = false,
  domainDetails,
}: CustomerDetailsFormProps) => {
  const { showToast } = useToast()
  const { cache } = useSWRConfig()
  const { isCustomerLoggedIn, loggedInCustomerDetails } = useAuth()
  const [countryCode, setCountryCode] = useState<string>("")
  const [isError, setIsError] = useState("")
  const [customerMembershipId, setCustomerMemberShipId] = useState("")
  const [showCustomerLoginSection, setShowCustomerLoginSection] = useState(false)
  const {
    data: productFieldsData,
    isLoading: isProductFieldsDataLoading,
    isValidating: isProductFieldsDataValidating,
    mutate: refetchproductFieldsData,
  } = useSWR(
    selectedProductId ? `productFieldsData-${selectedProductId}` : null,
    async () => {
      try {
        const selectProductId = selectedProductId || ""
        const { data: getProductFieldsResponse } = await OrderServices.getProductFields(
          selectProductId,
          isIntegratedPage
        )
        if (getProductFieldsResponse) {
          return getProductFieldsResponse.data?.map((field) => {
            if (field?.field?.type === "agreement") {
              return {
                ...field,
                label: "",
              }
            }
            return field
          })
        }
        return []
      } catch (e: unknown) {
        Sentry.captureException(e)
        const apiError = e as { response?: { data?: { message?: string } } }
        console.error(apiError.response?.data?.message)
        showToast({ heading: apiError.response?.data?.message, type: "danger" })
        return []
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  useEffect(() => {
    if (!isProductFieldsDataValidating) {
      cache.delete(`productFieldsData-${selectedProductId}`)
      refetchproductFieldsData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId])

  const initialValues = useMemo<PerOrderFieldsFormAttributes | undefined>(() => {
    if (productFieldsData) {
      const participantsFieldsArray: Record<string, boolean | string | string[] | number>[] = []
      const fields: Record<string, boolean | string | string[] | number> = {}
      productFieldsData.forEach((productField) => {
        if (productField.allowed_online_booking && productField.ask?.includes("ask_per_order")) {
          const fieldName = productField.field.name as string
          const defaultValue = productField.field.type === "checkbox" ? false : ""
          const customerValue =
            loggedInCustomerDetails?.[fieldName as keyof ILoggedInCustomerDetails]

          if (fieldName === "phone") {
            const phoneCode = loggedInCustomerDetails?.phone_code
            fields[fieldName] = phoneCode
              ? `+${phoneCode} ${customerValue || ""}`
              : customerValue || ""
            fields[`${fieldName}_id`] = productField.id ?? ""
          } else {
            fields[fieldName] =
              typeof customerValue !== "undefined" ? (customerValue ?? defaultValue) : defaultValue
            fields[`${fieldName}_id`] = productField.id ?? ""
          }
        }
      })
      participantsFieldsArray.push(fields)
      return {
        perOrderFieldsDetails: participantsFieldsArray,
      }
    }
    return {
      perOrderFieldsDetails: [],
    }
  }, [productFieldsData, loggedInCustomerDetails])

  const onSubmit = async (
    values: PerOrderFieldsFormAttributes,
    { setSubmitting, setFieldError }: FormikHelpers<PerOrderFieldsFormAttributes>
  ) => {
    try {
      // setSubmitting(true)
      if (
        values.perOrderFieldsDetails?.length &&
        Object.keys(values.perOrderFieldsDetails[0]).length
      ) {
        const customerDetails: { [key: string]: string | number | boolean | string[] } =
          values.perOrderFieldsDetails.map((perOrderFieldDetails) =>
            Object.entries(perOrderFieldDetails).reduce(
              (acc: { [key: string]: string | number | boolean | string[] }, [key, value]) => {
                if (key === "first-name" || key === "first_name") {
                  acc.first_name = value
                } else if (key === "last-name" || key === "last_name") {
                  acc.last_name = value
                } else if (key === "phone" || key === "email") {
                  acc[key] = value
                }
                return acc
              },
              {}
            )
          )[0]

        if (isCustomerLoggedIn) {
          onSuccess?.(loggedInCustomerDetails?.id, values, customerMembershipId)
        } else {
          const { data: addCustomerResponse } = await OrderServices.addCustomer(
            {
              ...customerDetails,
              phone: customerDetails.phone.slice(countryCode.length),
              phone_code: countryCode,
              status: "active",
              hasMembershipProduct,
            },
            isIntegratedPage
          )
          onSuccess?.(addCustomerResponse.customer.id, values, customerMembershipId)
        }
      }
    } catch (e: unknown) {
      Sentry.captureException(e)
      const apiError = e as { response?: { data?: { message?: string } }; message: string }
      const errors = apiError?.response?.data?.message
      if (errors?.includes("email")) {
        setFieldError("perOrderFieldsDetails[0].email", apiError?.response?.data?.message)
      } else {
        setIsError(
          apiError?.response?.data?.message ??
            apiError?.message ??
            "Unable to create customer! please try again later"
        )
      }
    }
  }

  if (isProductFieldsDataLoading && isProductFieldsDataValidating)
    return (
      <div className={"py-5 text-center"}>
        <Spinner size={"md"} light />
      </div>
    )

  return (
    <>
      {isIntegratedPage &&
      domainDetails?.enable_membership &&
      domainDetails?.enable_customer_login &&
      !hideVerificationSection ? (
        <>
          <div className={"flex items-center gap-2 mb-3"}>
            <Checkbox
              name={`member-old-customer`}
              id={`member-old-customer`}
              checked={showCustomerLoginSection || isCustomerLoggedIn}
              onChange={(e) => {
                const value = e.target.checked
                setShowCustomerLoginSection(value)
              }}
            />
            <Label htmlFor={`member-old-customer`} className={"text-neutral-900 text14Regular"}>
              Do you want to apply membership?
            </Label>
          </div>
          {showCustomerLoginSection || isCustomerLoggedIn ? (
            <VerifyMembershipForm
              isIntegratedPage={isIntegratedPage}
              showMembershipDropdown={false}
            />
          ) : null}
        </>
      ) : null}

      {productFieldsData && initialValues ? (
        <>
          {isError && (
            <Alert color={"failure"} className={"mb-3"}>
              <span>{isError}</span>
            </Alert>
          )}
          <Formik
            initialValues={initialValues}
            enableReinitialize
            innerRef={customerDetailsRef}
            validationSchema={() =>
              Yup.lazy((formValues: PerOrderFieldsFormAttributes) => {
                if (productFieldsData) {
                  const fields: Record<string, any> = {}
                  productFieldsData
                    .filter(
                      (field) =>
                        checkDisplayRules({
                          field,
                          formValues: formValues?.perOrderFieldsDetails?.[0],
                        }) === "show"
                    )
                    .forEach((productField) => {
                      if (
                        productField.allowed_online_booking &&
                        productField.ask?.includes("ask_per_order")
                      ) {
                        if (productField.field.type === "checkbox") {
                          fields[productField.field.name] = productField.required_for_online_booking
                            ? Yup.array()
                                .of(Yup.string())
                                .required(`*${productField.label} Required`)
                                .min(1)
                            : Yup.array().of(Yup.string())
                        } else if (productField.field.type === "inline") {
                          fields[productField.field.name] = Yup.string().optional()
                        } else if (productField.field.name === "email") {
                          fields[productField.field.name] = Yup.string()
                            .email(`*Please enter your email.`)
                            .required("*Please enter your email")
                        } else {
                          let fieldSchema = Yup.string()
                          if (productField.field.name === "phone") {
                            fieldSchema = fieldSchema
                              .required("*Phone Number is required")
                              .min(10, "Phone number must be at least 10 characters")
                              .max(13, "Phone number cannot exceed 13 characters")
                          } else {
                            fieldSchema = productField.required_for_online_booking
                              ? fieldSchema.required(`*${productField.label} Required`)
                              : fieldSchema
                          }
                          fields[productField.field.name] = fieldSchema
                        }
                      }
                    })
                  return Yup.object().shape({
                    perOrderFieldsDetails: Yup.array()
                      .required()
                      .min(1, "*Atleast one is Required")
                      .of(Yup.object().shape(fields)),
                  })
                }
                return Yup.object().shape({
                  perOrderFieldsDetails: Yup.array().of(Yup.object().shape({})),
                })
              })
            }
            onSubmit={onSubmit}
          >
            {({ getFieldProps, values, errors, touched }) => {
              return (
                <Form id={"perOrderFieldsForm"} autoComplete={"off"}>
                  <div className={"flex flex-col"}>
                    <div
                      className={
                        "px-6 pt-4 pb-8 shadow-sm rounded-md border border-neutral-450 bg-whiteBlack-100"
                      }
                    >
                      <FieldArray {...getFieldProps("perOrderFieldsDetails")}>
                        {() => {
                          if (!values.perOrderFieldsDetails?.length) return
                          return (
                            <>
                              {values.perOrderFieldsDetails.map((perOrderFieldsDetails, index) => {
                                return (
                                  <div
                                    className={
                                      "sm:grid flex flex-col sm:grid-cols-2 sm:gap-4 gap-2"
                                    }
                                    key={index}
                                  >
                                    <ParticipantsFields
                                      fieldsData={
                                        productFieldsData.filter(
                                          (field) =>
                                            checkDisplayRules({
                                              field,
                                              formValues: perOrderFieldsDetails,
                                            }) === "show"
                                        ) ?? []
                                      }
                                      fieldGroupFormName={"perOrderFieldsDetails"}
                                      fieldArrayIndex={0}
                                      type={"ask_per_order"}
                                      onPhoneInputChange={(value, country) => {
                                        setCountryCode(country.dialCode)
                                      }}
                                      isWidgetPage
                                    />
                                  </div>
                                )
                              })}
                            </>
                          )
                        }}
                      </FieldArray>
                    </div>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </>
      ) : null}
    </>
  )
}

export default CustomerDetailsForm
